const defaultRegex = /[^ -~]/gi
const DEFAULT_ROOT_FONT_SIZE = 16

const toCssPropertyName = (prop = '') => prop.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)

const registerFallback = (fallbacks, prop, value) => {
    if (!fallbacks || value == null) {
        return
    }

    const cssName = toCssPropertyName(prop)
    fallbacks[prop] = { cssName, value }
}

const formatRem = (px, base = DEFAULT_ROOT_FONT_SIZE) => {
    if (!Number.isFinite(px) || px === 0) {
        return px === 0 ? '0' : undefined
    }

    const rem = px / base
    const rounded = parseFloat(rem.toFixed(4))
    return `${rounded.toString().replace(/\.0+$/, '')}rem`
}

const cssLengthToPx = (value, base = DEFAULT_ROOT_FONT_SIZE) => {
    if (value == null) {
        return 0
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0
    }

    if (typeof value !== 'string') {
        return 0
    }

    const trimmed = value.trim().toLowerCase()
    if (!trimmed) {
        return 0
    }

    if (trimmed === '0') {
        return 0
    }

    if (trimmed.endsWith('px')) {
        const numeric = parseFloat(trimmed)
        return Number.isFinite(numeric) ? numeric : 0
    }

    if (trimmed.endsWith('rem')) {
        const numeric = parseFloat(trimmed)
        return Number.isFinite(numeric) ? numeric * base : 0
    }

    const fallback = parseFloat(trimmed)
    return Number.isFinite(fallback) ? fallback : 0
}

const expandPadding = (values = []) => {
    const padded = values.slice(0, 4)

    switch (padded.length) {
        case 0:
            return [0, 0, 0, 0]
        case 1:
            return [padded[0], padded[0], padded[0], padded[0]]
        case 2:
            return [padded[0], padded[1], padded[0], padded[1]]
        case 3:
            return [padded[0], padded[1], padded[2], padded[1]]
        default:
            return [padded[0], padded[1], padded[2], padded[3]]
    }
}

const normalizeLength = (value, prop, fallbacks, base = DEFAULT_ROOT_FONT_SIZE) => {
    if (value == null) {
        return value
    }

    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            return value
        }

        const fallback = `${value}px`
        const remValue = formatRem(value, base)

        if (remValue) {
            registerFallback(fallbacks, prop, fallback)
        }

        return remValue || fallback
    }

    if (typeof value !== 'string') {
        return value
    }

    const trimmed = value.trim()
    const lower = trimmed.toLowerCase()

    if (lower.endsWith('rem')) {
        return value
    }

    if (lower.endsWith('px') || /^-?\d+(?:\.\d+)?$/.test(lower)) {
        const px = cssLengthToPx(value, base)
        const remValue = formatRem(px, base)

        if (remValue) {
            const fallbackValue = lower.endsWith('px') ? trimmed : `${px}px`
            registerFallback(fallbacks, prop, fallbackValue)
        }

        return remValue || value
    }

    return value
}

const normalizePadding = (value, fallbacks, base = DEFAULT_ROOT_FONT_SIZE) => {
    if (value == null) {
        return { normalized: value, totalPx: 0 }
    }

    if (typeof value !== 'string') {
        if (typeof value === 'number' && Number.isFinite(value)) {
            const fallback = `${value}px`
            const remValue = formatRem(value, base)
            registerFallback(fallbacks, 'padding', fallback)
            const normalized = remValue || fallback
            const totalPx = value * 2
            return { normalized, totalPx }
        }

        return { normalized: value, totalPx: 0 }
    }

    const tokens = value.split(/\s+/).filter(Boolean)
    if (!tokens.length) {
        return { normalized: value, totalPx: 0 }
    }

    const pxValues = tokens.map(token => cssLengthToPx(token, base))
    const normalizedTokens = tokens.map((token, index) => {
        const px = pxValues[index]
        if (!Number.isFinite(px) || px === 0) {
            return px === 0 ? '0' : token
        }

        const remValue = formatRem(px, base)
        return remValue || token
    })

    const changed = normalizedTokens.some((token, index) => token !== tokens[index])
    if (changed) {
        registerFallback(fallbacks, 'padding', value)
    }

    const [ , right = 0, , left = 0 ] = expandPadding(pxValues)
    const totalPx = (Number.isFinite(right) ? right : 0) + (Number.isFinite(left) ? left : 0)

    return {
        normalized: normalizedTokens.join(' '),
        totalPx
    }
}

const prepareInputStyle = (style = {}) => {
    const base = typeof style.baseFontSize === 'number' && Number.isFinite(style.baseFontSize)
        ? style.baseFontSize
        : DEFAULT_ROOT_FONT_SIZE

    const merged = { ...style }
    delete merged.baseFontSize

    const fallbacks = {}

    const widthPx = cssLengthToPx(style.width, base)
    const fontSizePx = cssLengthToPx(style.fontSize, base)

    merged.width = normalizeLength(style.width, 'width', fallbacks, base)
    merged.height = normalizeLength(style.height, 'height', fallbacks, base)
    merged.minWidth = normalizeLength(style.minWidth, 'minWidth', fallbacks, base)
    merged.maxWidth = normalizeLength(style.maxWidth, 'maxWidth', fallbacks, base)
    merged.minHeight = normalizeLength(style.minHeight, 'minHeight', fallbacks, base)
    merged.maxHeight = normalizeLength(style.maxHeight, 'maxHeight', fallbacks, base)
    merged.fontSize = normalizeLength(style.fontSize, 'fontSize', fallbacks, base)
    merged.lineHeight = normalizeLength(style.lineHeight, 'lineHeight', fallbacks, base)

    const paddingInfo = normalizePadding(style.padding, fallbacks, base)
    merged.padding = paddingInfo.normalized

    return {
        style: merged,
        fallbacks,
        metrics: {
            widthPx,
            fontSizePx: fontSizePx || base,
            horizontalPaddingPx: paddingInfo.totalPx,
            baseFontSize: base
        }
    }
}


export default class TextInput extends Phaser.GameObjects.DOMElement {

    constructor(scene, x, y, type, style, callback = () => {}, maxLength = 100, preventTab = true, regex = defaultRegex) {
        let element = document.createElement('input')

        // Combine default styles with passed in styles
        const mergedStyle = {
            backgroundColor: 'transparent',
            border: 'transparent',
            outline: 'transparent',
            pointerEvents: 'auto',

            ...style
        }

        const { style: normalizedStyle, fallbacks, metrics } = prepareInputStyle(mergedStyle)

        element.type = type
        element.autocomplete = 'new-password'
        element.spellcheck = false
        element.maxLength = maxLength

        super(scene, x, y, element, normalizedStyle)

        this.applyResponsiveFallbacks(fallbacks, normalizedStyle)

        this.baseFontSize = metrics.baseFontSize

        this.callback = callback
        this.maxLength = maxLength
        this.preventTab = preventTab
        this.regex = regex

        const fixedWidth = Math.max(0, Math.round((Number.isFinite(metrics.widthPx) ? metrics.widthPx : 0) - (Number.isFinite(metrics.horizontalPaddingPx) ? metrics.horizontalPaddingPx : 0)))
        const fontSizePx = Number.isFinite(metrics.fontSizePx) && metrics.fontSizePx > 0 ? metrics.fontSizePx : DEFAULT_ROOT_FONT_SIZE

        // Phaser Text object which will be used on focus lost
        this.phaserText = this.scene.add.text(x, y, '', {
            fixedWidth,
            fontFamily: normalizedStyle.fontFamily,
            fontSize: `${fontSizePx}px`,
            color: normalizedStyle.color
        })

        this.phaserText.setOrigin(0.5, 0.5)
        this.phaserText.visible = false

        this.addListeners()
        this.addInput()
    }

    get text() {
        return this.node.value
    }

    setText(text = this.text) {
        this.node.value = this.filterText(text)
    }

    filterText(text) {
        return text.replace(this.regex, '').substring(0, this.maxLength)
    }

    applyResponsiveFallbacks(fallbacks, normalizedStyle) {
        if (!fallbacks) {
            return
        }

        for (const [property, metadata] of Object.entries(fallbacks)) {
            if (!metadata || metadata.value == null) {
                continue
            }

            const finalValue = normalizedStyle[property]

            const cssName = metadata.cssName || toCssPropertyName(property)

            this.node.style.setProperty(cssName, metadata.value)

            if (finalValue != null) {
                this.node.style.setProperty(cssName, finalValue)
            }
        }
    }

    /**
     * Gets total width of padding, requires padding to be in the following format:
     * "1px 1px 1px 1px".
     *
     * @param {string} padding - CSS padding string
     */
    getTotalPadding(padding) {
        const { totalPx } = normalizePadding(padding, null, this.baseFontSize || DEFAULT_ROOT_FONT_SIZE)
        return Number.isFinite(totalPx) ? totalPx : 0
    }

    addListeners() {
        this.addListener('focus')
        this.addListener('blur')
        this.addListener('keydown')
        this.addListener('input')

        this.scene.events.on('showinput', () => this.onShow())
        this.scene.events.on('hideinput', () => this.onHide())

        this.on('destroy', () => this.onDestroy())
    }

    addInput() {
        this.on('focus', () => this.onFocus())
        this.on('blur', () => this.onBlur())
        this.on('keydown', (event) => this.onKeyDown(event))
        this.on('input', () => this.setText())
    }

    onFocus() {
        this.clearEmoteKey()
        this.scene.game.input.keyboard.enabled = false
    }

    onBlur() {
        this.clearEmoteKey()
        this.scene.game.input.keyboard.enabled = true
    }

    onKeyDown(event) {
        if (event.key == 'Enter') {
            event.preventDefault()

            if (!this.text) {
                this.node.blur()
            }

            this.callback()

        } else if (event.key == 'Tab' && this.preventTab) {
            // Prevent default to stop tab switching elements
            event.preventDefault()
        }
    }

    onShow() {
        this.phaserText.visible = false
        this.visible = true
    }

    onHide() {
        // Update phaser text
        let text = (this.node.type == 'password') ? '•'.repeat(this.text.length) : this.text
        this.phaserText.text = text

        this.phaserText.visible = true
        this.visible = false
    }

    onDestroy() {
        this.scene.events.off('showinput')
        this.scene.events.off('hideinput')
    }

    setFocus() {
        this.node.focus()
    }

    clearText() {
        this.setText('')
    }

    clearEmoteKey() {
        if (this.scene.world.client) {
            this.scene.world.client.emoteKeyPressed = false
        }
    }

}
